import { defineStore } from 'pinia'

import {
  windowHeight,
  windowWidth,
} from '../services/window_utils/window_utils'

import { useInstanceStore } from 'src/stores/instance.js'
import { useMergedConfigStore } from 'src/stores/merged_config.js'
import { useOAuthStore } from 'src/stores/oauth.js'
import { useStreamingStore } from 'src/stores/streaming.js'
import { useSyncConfigStore } from 'src/stores/sync_config.js'
import { useUsersStore } from 'src/stores/users.js'

import { WSConnectionStatus } from 'src/api/websocket.js'
import {
  applyStaticTheme,
  DEFAULT_PALETTES,
} from 'src/services/static_theme/static_theme.js'
import {
  registerPushNotifications,
  unregisterPushNotifications,
} from 'src/services/sw/sw.js'
import {
  effectiveThemeMode,
  schemeForMode,
  systemPrefersDark,
  watchSystemTheme,
} from 'src/services/theme_mode/theme_mode.js'

const getNotificationPermission = async () => {
  const Notification = window.Notification

  if (!Notification) return null
  if (Notification.permission === 'default')
    return Notification.requestPermission()
  return Notification.permission
}

// how long a dropped socket may take to come back before it is reported
const STREAM_LOST_GRACE_MS = 10000
const streamLost = { timer: null, notice: null }

export const useInterfaceStore = defineStore('interface', {
  state: () => ({
    themeApplied: false,
    stopWatchingSystemTheme: null,
    temporaryChangesTimeoutId: null,
    temporaryChangesCountdown: -1, // used for temporary options that revert after a timeout
    temporaryChangesConfirm: () => {
      /* no-op */
    }, // used for applying temporary options
    temporaryChangesRevert: () => {
      /* no-op */
    }, // used for reverting temporary options
    settingsModalState: 'hidden',
    settingsModalLoadedUser: false,
    settingsModalLoadedAdmin: false,
    settingsModalTargetTab: null,
    settingsModalMode: 'user',
    settings: {
      currentSaveStateNotice: null,
      noticeClearTimeout: null,
      notificationPermission: null,
    },
    browserSupport: {
      cssFilter:
        window.CSS?.supports &&
        (window.CSS.supports('filter', 'drop-shadow(0 0)') ||
          window.CSS.supports('-webkit-filter', 'drop-shadow(0 0)')),
    },
    layoutType: 'normal',
    globalNotices: [],
    globalError: null,
    layoutHeight: 0,
    lastTimeline: null,
  }),
  getters: {
    simpleThemeMode() {
      return effectiveThemeMode(useMergedConfigStore().mergedConfig)
    },
  },

  actions: {
    attachSocket() {
      const et = new EventTarget()
      const socket = {
        name: 'interface',
        et,
      }

      et.addEventListener('open', this.onStreamConnect)
      et.addEventListener('close', this.onStreamDisconnect)

      useStreamingStore().addSubscriber(socket)
    },
    onLogin() {
      getNotificationPermission().then((permission) =>
        useInterfaceStore().setNotificationPermission(permission),
      )

      this.setLayoutWidth(windowWidth())
      this.setLayoutHeight(windowHeight())
    },
    onLogout() {
      this.setLastTimeline('public-timeline')
      this.setLayoutWidth(windowWidth())
      this.setLayoutHeight(windowHeight())
    },
    // A dropped socket usually comes back at once (browsers drop the
    // sockets of background tabs): only one still down after a grace
    // period is reported, and only then is its return.
    onStreamConnect() {
      clearTimeout(streamLost.timer)
      streamLost.timer = null
      if (!streamLost.notice) return
      this.removeGlobalNotice(streamLost.notice)
      streamLost.notice = null
      this.pushGlobalNotice({
        level: 'success',
        messageKey: 'timeline.socket_reconnected',
        timeout: 5000,
      })
    },
    onStreamDisconnect(closeEvent) {
      // never connected: nothing broke (a server that refuses sockets from
      // here is polled instead)
      if (!useStreamingStore().everOpened) return
      const intendedCodes = new Set([
        1000, // Normal (intended) closure
        1001, // Going away
      ])
      const { code } = closeEvent.original
      if (intendedCodes.has(code) || streamLost.timer || streamLost.notice) {
        return
      }
      streamLost.timer = setTimeout(() => {
        streamLost.timer = null
        if (useStreamingStore().state === WSConnectionStatus.JOINED) return
        streamLost.notice = this.pushGlobalNotice({
          level: 'error',
          messageKey: 'timeline.socket_broke',
          messageArgs: [code],
          timeout: 0,
        })
      }, STREAM_LOST_GRACE_MS)
    },
    setTemporaryChanges({ confirm, revert }) {
      this.temporaryChangesCountdown = 10
      this.temporaryChangesConfirm = confirm
      this.temporaryChangesRevert = revert
      const countdownFunc = () => {
        if (this.temporaryChangesCountdown <= 1) {
          this.temporaryChangesRevert()
          this.clearTemporaryChanges()
        } else {
          this.temporaryChangesCountdown--
          this.temporaryChangesTimeoutId = setTimeout(countdownFunc, 1000)
        }
      }
      this.temporaryChangesTimeoutId = setTimeout(countdownFunc, 1000)
    },
    clearTemporaryChanges() {
      this.temporaryChangesTimeoutId ??
        clearTimeout(this.temporaryChangesTimeoutId)
      this.temporaryChangesTimeoutId = null
      this.temporaryChangesCountdown = -1
      this.temporaryChangesConfirm = () => {
        /* no-op */
      }
      this.temporaryChangesRevert = () => {
        /* no-op */
      }
    },
    setPageTitle(option = '') {
      try {
        document.title = `${option} ${useInstanceStore().instanceIdentity.name}`
      } catch (error) {
        console.error(`${error}`)
      }
    },
    settingsSaved({ success, error }) {
      if (success) {
        if (this.noticeClearTimeout) {
          clearTimeout(this.noticeClearTimeout)
        }
        this.settings.currentSaveStateNotice = { error: false, data: success }
        this.settings.noticeClearTimeout = setTimeout(
          () => delete this.settings.currentSaveStateNotice,
          2000,
        )
      } else {
        this.settings.currentSaveStateNotice = { error: true, errorData: error }
      }
    },
    setNotificationPermission(permission) {
      this.notificationPermission = permission
    },
    closeSettingsModal() {
      this.settingsModalState = 'hidden'
    },
    openSettingsModal(value) {
      this.settingsModalMode = value
      this.settingsModalState = 'visible'
      if (value === 'user') {
        if (!this.settingsModalLoadedUser) {
          this.settingsModalLoadedUser = true
        }
      } else if (value === 'admin') {
        if (!this.settingsModalLoadedAdmin) {
          this.settingsModalLoadedAdmin = true
        }
      }
    },
    setSettingsModalState(newState) {
      const oldState = this.settingsModalState
      const legal = (() => {
        switch (oldState) {
          case 'minimized':
            return true
          case 'visible':
            return true
          case 'hidden':
            return newState === 'visible'
        }
      })()

      if (legal) {
        this.settingsModalState = newState
      }
    },
    toggleMinimizeSettingsModal() {
      switch (this.settingsModalState) {
        case 'minimized':
          this.settingsModalState = 'visible'
          return
        case 'visible':
          this.settingsModalState = 'minimized'
          return
        case 'hidden':
          return
        default:
          throw new Error(
            `Illegal minimization state of settings modal: ${this.settingsModalState}`,
          )
      }
    },
    clearSettingsModalTargetTab() {
      this.settingsModalTargetTab = null
    },
    openSettingsModalTab(value, mode = 'user') {
      this.settingsModalTargetTab = value
      this.openSettingsModal(mode)
    },
    removeGlobalNotice(notice) {
      this.globalNotices = this.globalNotices.filter((n) => n !== notice)
    },
    setGlobalError({ error, instance, info }) {
      switch (info) {
        case 'https://vuejs.org/error-reference/#runtime-13': {
          this.globalError = {
            title: 'general.refresh_required',
            content: 'general.refresh_required_content',
            // `true` disables cache on Firefox (non-standard)
            recover: () => window.location.reload(true),
            recoverText: 'general.refresh_required_refresh',
            error,
          }
          break
        }
        default: {
          this.globalError = { error }
          break
        }
      }
    },
    clearGlobalError() {
      this.globalError = null
    },
    // error: the failure behind it, if any. An unreachable server (a
    // restart, most likely) gets a calm text instead of the caller's.
    pushGlobalNotice({
      messageKey,
      messageArgs = {},
      level = 'error',
      timeout = 5000,
      error,
      // { labelKey, run }: a button next to the message
      action,
    }) {
      if (error?.serverUnavailable) {
        messageKey = 'general.server_unavailable'
        messageArgs = [error.statusCode]
      }
      const notice = {
        messageKey,
        messageArgs,
        level,
        action,
      }

      // several pollers fail together (a server restart): one notice
      const same = this.globalNotices.find(
        (n) =>
          n.messageKey === messageKey &&
          n.level === level &&
          JSON.stringify(n.messageArgs) === JSON.stringify(messageArgs),
      )
      if (same) return same

      this.globalNotices.push(notice)

      // Adding a new element to array wraps it in a Proxy, which breaks the comparison
      // TODO: Generate UUID or something instead or relying on !== operator?
      const newNotice = this.globalNotices[this.globalNotices.length - 1]
      if (timeout > 0) {
        setTimeout(() => this.removeGlobalNotice(newNotice), timeout)
      }

      return newNotice
    },
    setLayoutHeight(value) {
      this.layoutHeight = value
    },
    setLayoutWidth(value) {
      let width = value
      if (value !== undefined) {
        this.layoutWidth = value
      } else {
        width = this.layoutWidth
      }

      const mobileLayout = width <= 800
      const normalOrMobile = mobileLayout ? 'mobile' : 'normal'
      const { thirdColumnMode } = useMergedConfigStore().mergedConfig
      if (thirdColumnMode === 'none' || !useUsersStore().currentUser) {
        this.layoutType = normalOrMobile
      } else {
        const wideLayout = width >= 1300
        this.layoutType = wideLayout ? 'wide' : normalOrMobile
      }
    },
    setLastTimeline(value) {
      this.lastTimeline = value
    },
    setThemeMode(mode) {
      useSyncConfigStore().setPreference({
        path: 'simple.themeMode',
        value: mode,
      })
      useSyncConfigStore().pushSyncConfig()

      this.applyTheme()
    },
    async setThemeApplied() {
      this.themeApplied = true
    },
    // Re-theme when the operating system flips between light and dark,
    // but only while the simple automatic mode is in charge.
    watchSystemTheme() {
      this.stopWatchingSystemTheme?.()
      this.stopWatchingSystemTheme = watchSystemTheme(() => {
        if (
          effectiveThemeMode(useMergedConfigStore().mergedConfig) !== 'auto'
        ) {
          return
        }
        this.applyTheme()
      })
    },
    // The light or dark palette as custom properties on the root; the
    // static stylesheet derives everything else. Returns a promise so
    // callers can chain on it.
    applyTheme() {
      const scheme = schemeForMode(this.simpleThemeMode, systemPrefersDark())
      applyStaticTheme(DEFAULT_PALETTES[scheme])
      this.setThemeApplied()
      return Promise.resolve()
    },

    // Push notifications
    registerPushNotifications() {
      // hosted: the service worker would ask this site, not the instance,
      // for the pushed notification
      if (useInstanceStore().hosted) return
      const token = useOAuthStore().token
      const vapidPublicKey = useInstanceStore().vapidPublicKey
      const isEnabled = useMergedConfigStore().mergedConfig.webPushNotifications
      const notificationVisibility =
        useMergedConfigStore().mergedConfig.notificationVisibility

      registerPushNotifications(
        isEnabled,
        vapidPublicKey,
        token,
        notificationVisibility,
      )
    },

    unregisterPushNotifications() {
      const token = useOAuthStore().token

      unregisterPushNotifications(token)
    },
  },
})
